import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { UserNote } from '../users/types';
import { attachIds } from '../utils';

import {
  ShortStudent,
  Student,
  StudentAnalytics,
} from './types';
import { toShortStudents, toStudent } from './utils';

// Define a service using a base URL and expected endpoints
export const studentsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getStudent: build.query<CognitoAPIResponse<Student>, {studentId: string}>({
      query: (arg) => `students/${arg?.studentId}`,
      providesTags: (result, error, arg) => [{ type: 'Students', id: arg?.studentId }],
      transformResponse(response: any) {
        return toStudent(response);
      },
    }),
    getStudents: build.query<CognitoAPIResponse<ShortStudent[]>, {
      search?: string,
      excludeDisabled?: boolean,
      gradYear?: number,
      state?: string,
    }>({
      query: (arg) => {
        const { excludeDisabled, gradYear, search, state } = arg;
        return {
          url: 'students/query',
          method: 'GET',
          params: {
            excludeDisabled, gradYear, search, state,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Students' as const, id })), 'Students']
        : ['Students']),
      transformResponse(response: any) {
        return toShortStudents(response);
      },
    }),
    updateStudent: build.mutation<any, {studentId: string, newAttributes: any}>({
      query(arg) {
        const { studentId, newAttributes } = arg;
        return {
          url: `students/${studentId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Invalidates the student but also all Lessons and
      // LessonEnrolments for that student if the student's
      // attendanceType is updated
      invalidatesTags: (result, error, arg) => (arg.newAttributes.attendanceType
        ? [{ type: 'Students', id: arg.studentId }, { type: 'LessonEnrolments', id: arg.studentId }, 'Lessons']
        : [{ type: 'Students', id: arg.studentId }]),
    }),
    getStudentAnalytics: build.query<StudentAnalytics,
    {studentId: string, subject: string, topic?: string}>({
      query: (arg) => {
        const { studentId, subject, topic } = arg;
        return {
          url: `students/${studentId}/analytics`,
          method: 'GET',
          params: {
            subject, topic,
          },
        };
      },
      providesTags: (result, error, arg) => [{ type: 'StudentAnalytics', id: arg?.studentId }],
    }),
    newStudentNote: build.mutation<any, {
      studentId: string,
      note: string,
      attributes?: {
        assignee?: string,
        dueDatetime?: string,
        billingRelated?: boolean
      }
    }>({
      query(arg) {
        const { studentId, note, attributes } = arg;

        return {
          url: `students/${studentId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getStudentNotes: build.query<CognitoAPIResponse<UserNote[]>, {
      studentId: string,
      active?: boolean,
      billingRelated?: boolean,
    }>({
      query(arg) {
        const { studentId, active, billingRelated } = arg;

        return {
          url: `students/${studentId}/notes/query`,
          params: { active, billingRelated },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'UserNotes' as const, id })), 'UserNotes']
        : ['UserNotes']),
      transformResponse(response: any) {
        return attachIds(response, (entry: UserNote) => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    updateStudentNote: build.mutation<any, {
      studentId: string,
      creationDatetime: number,
      newAttributes: any
    }>({
      query(arg) {
        const { studentId, creationDatetime, newAttributes } = arg;

        return {
          url: `students/${studentId}/notes/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (result, error, arg) => (arg.newAttributes.dueDatetime
        ? ['UserNotes']
        : [{ type: 'UserNotes', id: `${arg.studentId}#${arg.creationDatetime}` }]),
    }),
    deleteStudentNote: build.mutation<any, {
      studentId: string,
      creationDatetime: number,
    }>({
      query(arg) {
        const { studentId, creationDatetime } = arg;

        return {
          url: `students/${studentId}/notes/${creationDatetime}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'UserNotes', id: `${arg.studentId}#${arg.creationDatetime}` },
      ],
    }),
    updateCredits: build.mutation<any, {
      studentId: string,
      subject: string,
      course: string,
      credits: {
        termCredits?: number,
        extraCredits?: number
      }
    }>({
      query(arg) {
        const { studentId, subject, course, credits } = arg;

        return {
          url: `students/${studentId}/credits/${subject}/${course}/update`,
          body: credits,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Students', id: arg.studentId },
      ],
    }),
    newInvoice: build.mutation<any, {
      studentId: string,
      auto?: boolean,
    }>({
      query(arg) {
        const { studentId, auto } = arg;

        return {
          url: `students/${studentId}/invoices/new`,
          method: 'POST',
          body: { auto },
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Students', id: arg.studentId },
      ],
    }),
  }),
});

export const {
  useLazyGetStudentQuery,
  useGetStudentQuery,
  useGetStudentsQuery,
  useUpdateStudentMutation,
  useGetStudentAnalyticsQuery,
  useDeleteStudentNoteMutation,
  useGetStudentNotesQuery,
  useNewStudentNoteMutation,
  useUpdateStudentNoteMutation,
  useUpdateCreditsMutation,
  useNewInvoiceMutation,
} = studentsApi;
